import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { PageTitle } from './PageTitle';
import { DETAIL_STATS, PLAYER_STATS_LEVELS, STATS_LEVELS, YOUR_STATS } from './statisticsConfig';

import { useApplication } from '../AppContext';
import ComputerConfig from '../configs/ComputerConfig';

const COMPUTER_BY_NAME = new Map(ComputerConfig.map((computer) => [computer.name, computer]));
const DISPLAYED_PLAYER_NAMES = new Set(['You', ...COMPUTER_BY_NAME.keys()]);

function getValue(statSet, path, defaultValue = 0) {
	const value = path.split('.').reduce((current, part) => current?.[part], statSet);

	return value ?? defaultValue;
}

function formatValue(value, format) {
	if (format === 'percent') {
		return new Intl.NumberFormat(undefined, {
			style: 'percent',
			maximumFractionDigits: 1,
		}).format(value);
	}

	return value;
}

function compareBySkillThenName(a, b) {
	const skillRank = (skill) => (Number.isFinite(Number(skill)) ? Number(skill) : 0);

	return skillRank(a.skill) - skillRank(b.skill) || a.player.localeCompare(b.player);
}

function Avatar({ name, statSet }) {
	const avatar = COMPUTER_BY_NAME.get(name)?.avatar || statSet?.avatar;
	const source = avatar ? `assets/Avatars/vv/${avatar}` : null;

	return source ? (
		<img className="statsAvatar" src={source} alt={`${name} avatar`} />
	) : (
		<div className="statsAvatar statsAvatarFallback" aria-hidden="true">{name.charAt(0)}</div>
	);
}

function getDetailedStatSets(stats) {
	const players = Object.entries(stats).filter(([player]) => DISPLAYED_PLAYER_NAMES.has(player));
	const storedLevels = new Set(players.flatMap(([, playerStats]) => (
		playerStats && typeof playerStats === 'object'
			? Object.keys(playerStats).filter((level) => level !== 'All')
			: []
	)));
	const levels = [
		'Overall',
		...STATS_LEVELS.filter((level) => storedLevels.has(level)),
		...[...storedLevels].filter((level) => !STATS_LEVELS.includes(level)).sort(),
	];

	return levels.flatMap((level) => players
		.map(([player, playerStats]) => ({
			player,
			statSet: level === 'Overall' ? playerStats?.All : playerStats?.[level],
		}))
		.filter(({ statSet }) => statSet && typeof statSet === 'object' && Object.keys(statSet).length)
		.map(({ player, statSet }) => ({
			...statSet,
			player,
			level,
		}))
		.sort(compareBySkillThenName));
}

function downloadStatistics(stats) {
	const file = new Blob([JSON.stringify(stats, null, 2)], {
		type: 'application/json',
	});
	const url = URL.createObjectURL(file);
	const link = document.createElement('a');

	link.href = url;
	link.download = `hearts-statistics-${new Date().toISOString().slice(0, 10)}.json`;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
}

const Statistics = () => {
	const { t } = useTranslation();
	const app = useApplication();
	const stats = app.userdata?.stats || {};
	const youByLevel = stats.You && typeof stats.You === 'object' ? stats.You : {};
	const yourLevelStats = PLAYER_STATS_LEVELS.map((level) => ({
		level,
		statSet: level === 'Overall' ? (youByLevel.All || {}) : (youByLevel[level] || {}),
	}));
	const detailedStatSets = getDetailedStatSets(stats);

	return (
		<div id="Stats1" className="page1 noAnimate statisticsPage">
			<div className="statisticsWrapper">
				<PageTitle>
					<h1>{t('Statistics')}</h1>
				</PageTitle>

				<section className="statsSection">
					<header className="statsCardHeader">
						<h2>Level</h2>
					</header>
					<div className="statsSummaryScroller">
						<table className="statsSummaryTable yourStatsTable">
							<thead>
								<tr>
									<th>Statistic</th>
									{yourLevelStats.map(({ level }) => (
										<th className={level === 'Overall' ? 'overallStatsColumn' : ''} key={level}>{level}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{YOUR_STATS.map((statistic) => (
									<tr key={statistic.path}>
										<th>{statistic.label}</th>
										{yourLevelStats.map(({ level, statSet }) => (
											<td className={level === 'Overall' ? 'overallStatsColumn' : ''} key={level}>
												{formatValue(getValue(statSet, statistic.path), statistic.format)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>

				<section className="detailedStats">
					<header className="detailedStatsHeader">
						<span>Detailed statistics</span>
					</header>
					<div className="statsTableScroller">
						<table className="statsTable">
							<tbody>
								{DETAIL_STATS.map((statistic) => (
									<tr key={statistic.path}>
										<th scope="row">{statistic.label}</th>
										{detailedStatSets.map((statSet) => {
											const key = `${statSet.player}-${statSet.level}`;

											return statistic.format === 'avatar'
												? <td key={key}><Avatar name={statSet.player} statSet={statSet} /></td>
												: <td key={key}>{formatValue(getValue(statSet, statistic.path), statistic.format)}</td>;
										})}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>

				<div className="statsExport">
					<button type="button" className="simpleButtonOutLine" onClick={() => downloadStatistics(stats)}>Export</button>
				</div>
			</div>
		</div>
	);
};

export default Statistics;
